// src/components/orders/ShipmentTracking.js

import React, { useState } from 'react';

// ─── Step config: maps shipment statuses to human-readable progress steps ────
// These are the 6 visual steps shown in the progress bar at the top.
// Each step can be reached by one or more raw Shiprocket statuses.
const STEPS = [
  {
    key: 'ORDERED',
    label: 'Order Placed',
    icon: '🛒',
    matchStatuses: ['PENDING']
  },
  {
    key: 'PICKUP_SCHEDULED',
    label: 'Pickup Scheduled',
    icon: '📅',
    matchStatuses: ['PICKUP_SCHEDULED', 'PICKUP_FAILED']
  },
  {
    key: 'PICKED_UP',
    label: 'Picked Up',
    icon: '📦',
    matchStatuses: ['PICKED_UP']
  },
  {
    key: 'IN_TRANSIT',
    label: 'In Transit',
    icon: '🚚',
    matchStatuses: ['IN_TRANSIT', 'REACHED_DESTINATION_HUB', 'DELAYED']
  },
  {
    key: 'OUT_FOR_DELIVERY',
    label: 'Out for Delivery',
    icon: '🏃',
    matchStatuses: ['OUT_FOR_DELIVERY', 'ATTEMPTED_DELIVERY', 'UNDELIVERED']
  },
  {
    key: 'DELIVERED',
    label: 'Delivered',
    icon: '✅',
    matchStatuses: ['DELIVERED']
  }
];

// Special terminal statuses that break the normal flow
const CANCELLED_STATUSES = ['CANCELLED', 'RETURNED_TO_ORIGIN', 'LOST', 'DAMAGED'];

// ─── Helpers ─────────────────────────────────────────────────────────────────

const getActiveStepIndex = (shipmentStatus) => {
  if (!shipmentStatus) return 0;
  const upper = shipmentStatus.toUpperCase();
  const idx = STEPS.findIndex(step =>
    step.matchStatuses.includes(upper)
  );
  return idx === -1 ? 0 : idx;
};

const isCancelled = (shipmentStatus) =>
  CANCELLED_STATUSES.includes((shipmentStatus || '').toUpperCase());

const getActivityIcon = (status) => {
  const s = (status || '').toUpperCase();
  if (s.includes('PICKED')) return '📦';
  if (s.includes('TRANSIT') || s.includes('HUB')) return '🚚';
  if (s.includes('OUT_FOR')) return '🏃';
  if (s.includes('DELIVERED')) return '✅';
  if (s.includes('ATTEMPT') || s.includes('UNDELIVERED')) return '🔔';
  if (s.includes('CANCELLED') || s.includes('RETURN')) return '↩️';
  if (s.includes('DELAYED')) return '⏳';
  if (s.includes('LOST') || s.includes('DAMAGED')) return '⚠️';
  if (s.includes('PICKUP')) return '📅';
  return '📍';
};

const getActivityColor = (status) => {
  const s = (status || '').toUpperCase();
  if (s.includes('DELIVERED')) return 'text-green-600';
  if (s.includes('CANCELLED') || s.includes('LOST') || s.includes('DAMAGED')) return 'text-red-500';
  if (s.includes('DELAYED') || s.includes('ATTEMPT') || s.includes('UNDELIVERED')) return 'text-yellow-600';
  if (s.includes('RETURN')) return 'text-orange-500';
  return 'text-gray-700';
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

const formatDateShort = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

// ─── Sub-components ───────────────────────────────────────────────────────────

const CourierInfoBar = ({ shipment }) => {
  if (!shipment) return null;

  const awb = shipment.courier?.trackingId || shipment.courier?.waybillNumber;
  const courierName = shipment.courier?.name || 'Shiprocket';
  const trackingUrl = shipment.courier?.trackingUrl;
  const eta = shipment.estimatedDelivery;

  return (
    <div className="bg-blue-50 border border-blue-100 rounded-lg p-4 mb-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex flex-col gap-1">
          <span className="text-xs text-blue-500 font-medium uppercase tracking-wide">
            Courier Partner
          </span>
          <span className="text-sm font-semibold text-gray-800">{courierName}</span>
        </div>

        {awb && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-blue-500 font-medium uppercase tracking-wide">
              AWB / Tracking ID
            </span>
            <span className="text-sm font-mono font-semibold text-gray-800">{awb}</span>
          </div>
        )}

        {eta && (
          <div className="flex flex-col gap-1">
            <span className="text-xs text-blue-500 font-medium uppercase tracking-wide">
              Estimated Delivery
            </span>
            <span className="text-sm font-semibold text-gray-800">
              {formatDateShort(eta)}
            </span>
          </div>
        )}

        {trackingUrl && (
          <a
            href={trackingUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 transition-colors"
          >
            Track on Courier Site ↗
          </a>
        )}
      </div>
    </div>
  );
};

const ProgressStepper = ({ currentStatus, cancelled }) => {
  const activeIdx = getActiveStepIndex(currentStatus);

  if (cancelled) {
    return (
      <div className="flex items-center justify-center py-6">
        <div className="flex flex-col items-center gap-2">
          <div className="w-12 h-12 rounded-full bg-red-100 flex items-center justify-center text-2xl">
            ↩️
          </div>
          <span className="text-sm font-medium text-red-600">
            {currentStatus === 'RETURNED_TO_ORIGIN'
              ? 'Returned to Origin'
              : currentStatus === 'LOST'
              ? 'Shipment Lost'
              : currentStatus === 'DAMAGED'
              ? 'Shipment Damaged'
              : 'Shipment Cancelled'}
          </span>
        </div>
      </div>
    );
  }

  return (
    <div className="relative mb-6">
      {/* Desktop: horizontal stepper */}
      <div className="hidden sm:flex items-start justify-between relative">
        {/* Connector line behind steps */}
        <div className="absolute top-5 left-0 right-0 h-0.5 bg-gray-200 z-0" />
        <div
          className="absolute top-5 left-0 h-0.5 bg-green-500 z-0 transition-all duration-500"
          style={{ width: `${(activeIdx / (STEPS.length - 1)) * 100}%` }}
        />

        {STEPS.map((step, idx) => {
          const isCompleted = idx < activeIdx;
          const isActive = idx === activeIdx;
          const isPending = idx > activeIdx;

          return (
            <div key={step.key} className="flex flex-col items-center z-10 flex-1">
              {/* Circle */}
              <div
                className={`w-10 h-10 rounded-full flex items-center justify-center text-lg border-2 transition-all duration-300
                  ${isCompleted
                    ? 'bg-green-500 border-green-500 text-white'
                    : isActive
                    ? 'bg-white border-green-500 text-green-600 shadow-md ring-2 ring-green-200'
                    : 'bg-white border-gray-300 text-gray-400'
                  }`}
              >
                {isCompleted ? '✓' : step.icon}
              </div>
              {/* Label */}
              <span
                className={`mt-2 text-xs text-center leading-tight max-w-[70px]
                  ${isCompleted ? 'text-green-600 font-medium'
                  : isActive ? 'text-green-700 font-semibold'
                  : 'text-gray-400'}`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>

      {/* Mobile: vertical stepper */}
      <div className="flex sm:hidden flex-col gap-3">
        {STEPS.map((step, idx) => {
          const isCompleted = idx < activeIdx;
          const isActive = idx === activeIdx;

          return (
            <div key={step.key} className="flex items-center gap-3">
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-sm border-2 flex-shrink-0
                  ${isCompleted
                    ? 'bg-green-500 border-green-500 text-white'
                    : isActive
                    ? 'bg-white border-green-500 text-green-600'
                    : 'bg-white border-gray-200 text-gray-300'
                  }`}
              >
                {isCompleted ? '✓' : step.icon}
              </div>
              <span
                className={`text-sm
                  ${isCompleted ? 'text-green-600 font-medium'
                  : isActive ? 'text-green-700 font-semibold'
                  : 'text-gray-400'}`}
              >
                {step.label}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const ActivityLog = ({ trackingHistory }) => {
  const [showAll, setShowAll] = useState(false);

  if (!trackingHistory || trackingHistory.length === 0) {
    return (
      <p className="text-sm text-gray-500 text-center py-4">
        No activity recorded yet. Updates will appear here once the shipment is picked up.
      </p>
    );
  }

  // Sort newest first
  const sorted = [...trackingHistory].sort(
    (a, b) => new Date(b.date || b.timestamp) - new Date(a.date || a.timestamp)
  );

  const visible = showAll ? sorted : sorted.slice(0, 4);

  return (
    <div>
      <div className="space-y-4">
        {visible.map((activity, idx) => (
          <div key={idx} className="flex gap-3">
            {/* Dot + line */}
            <div className="flex flex-col items-center">
              <div className={`text-base mt-0.5 ${getActivityColor(activity.status || activity.activity)}`}>
                {getActivityIcon(activity.status || activity.activity)}
              </div>
              {idx < visible.length - 1 && (
                <div className="w-px flex-1 bg-gray-200 mt-1 min-h-[20px]" />
              )}
            </div>

            {/* Content */}
            <div className="pb-4 flex-1">
              <p className={`text-sm font-medium ${getActivityColor(activity.status || activity.activity)}`}>
                {activity.activity || activity.status || 'Update'}
              </p>
              {activity.location && (
                <p className="text-xs text-gray-500 mt-0.5">
                  📍 {activity.location}
                </p>
              )}
              <p className="text-xs text-gray-400 mt-0.5">
                {formatDate(activity.date || activity.timestamp)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {sorted.length > 4 && (
        <button
          onClick={() => setShowAll(prev => !prev)}
          className="mt-2 text-xs text-blue-600 hover:text-blue-800 font-medium"
        >
          {showAll ? 'Show less ▲' : `Show all ${sorted.length} updates ▼`}
        </button>
      )}
    </div>
  );
};

// ─── Main Component ───────────────────────────────────────────────────────────

/**
 * ShipmentTracking
 *
 * Props:
 *  - shipment       {Object}  Shipment record from DB (courier, status, estimatedDelivery, timeline)
 *  - trackingData   {Object}  Live Shiprocket tracking response (trackingHistory, currentLocation, status)
 *  - loading        {Boolean} Show skeleton while data loads
 *  - error          {String}  Error message to display
 *
 * Usage:
 *   <ShipmentTracking
 *     shipment={shipment}
 *     trackingData={trackingData}
 *     loading={loading.tracking}
 *     error={error.tracking}
 *   />
 */
const ShipmentTracking = ({ shipment, trackingData, loading, error }) => {

  if (loading) {
    return (
      <div className="animate-pulse space-y-4 py-4">
        <div className="h-16 bg-gray-100 rounded-lg" />
        <div className="flex justify-between gap-2">
          {[...Array(6)].map((_, i) => (
            <div key={i} className="flex-1 h-14 bg-gray-100 rounded-lg" />
          ))}
        </div>
        <div className="space-y-3 mt-4">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-10 bg-gray-100 rounded" />
          ))}
        </div>
      </div>
    );
  }

  // No shipment yet — order hasn't been picked up
  if (!shipment && !trackingData) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-3">📦</div>
        <p className="text-gray-600 font-medium">Shipment not created yet</p>
        <p className="text-sm text-gray-400 mt-1">
          Tracking details will appear here once your order is picked up by the courier.
        </p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-4xl mb-3">⚠️</div>
        <p className="text-gray-600 font-medium">Could not load tracking info</p>
        <p className="text-sm text-gray-400 mt-1">{error}</p>
      </div>
    );
  }

  const currentStatus = trackingData?.status || shipment?.status || 'PENDING';
  const cancelled = isCancelled(currentStatus);

  // Prefer live tracking history, fall back to shipment timeline
  const activityLog =
    trackingData?.trackingHistory ||
    shipment?.timeline?.map(t => ({
      date: t.timestamp,
      activity: t.description || t.status,
      location: t.location,
      status: t.status
    })) ||
    [];

  const currentLocation = trackingData?.currentLocation || shipment?.shippingAddress?.city;

  return (
    <div>
      {/* Courier info bar */}
      <CourierInfoBar shipment={shipment} />

      {/* Current location pill */}
      {currentLocation && !cancelled && (
        <div className="flex items-center gap-2 mb-4">
          <span className="text-xs text-gray-500">Current location:</span>
          <span className="inline-flex items-center gap-1 text-xs bg-gray-100 text-gray-700 px-2 py-1 rounded-full font-medium">
            📍 {currentLocation}
          </span>
        </div>
      )}

      {/* Progress stepper */}
      <ProgressStepper currentStatus={currentStatus} cancelled={cancelled} />

      {/* Activity log */}
      <div className="mt-4">
        <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
          <span>Tracking Activity</span>
          {activityLog.length > 0 && (
            <span className="text-xs font-normal text-gray-400">
              ({activityLog.length} update{activityLog.length !== 1 ? 's' : ''})
            </span>
          )}
        </h4>
        <ActivityLog trackingHistory={activityLog} />
      </div>
    </div>
  );
};

export default ShipmentTracking;